import {
  CreateProductProjectPlanDialog,
  UpdateProductProjectPlanDialog,
  DeleteProductProjectPlanDialog,
} from './components';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProductProjectPlanItem } from 'library/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { alpha, useTheme, Button, Chip, Grid2 as Grid } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { DataGridBox, CustomToolbar, ZebraDataGrid, PermissionHandler, CustomNoRowsOverlay } from 'components';
import { STORAGE_KEY_ENUM, PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

const ProductProjectPlan = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [productProjectPlans, setProductProjectPlans] = useState<ProductProjectPlanItem[]>([]);
  const [openCreateProductProjectPlanDialog, setOpenCreateProductProjectPlanDialog] = useState(false);
  const [openUpdateProductProjectPlanDialog, setOpenUpdateProductProjectPlanDialog] = useState(false);
  const [openDeleteProductProjectPlanDialog, setOpenDeleteProductProjectPlanDialog] = useState(false);

  // dynamic variables
  const getProjectStepPriorityTypeTranslated = useCallback(
    (projectStepPriorityType: number) =>
      ({
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.nonUrgent',
          defaultMessage: 'Düşük',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.urgent',
          defaultMessage: 'Orta',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.veryUrgent',
          defaultMessage: 'Yüksek',
        }),
      })[projectStepPriorityType],
    [intl]
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT),
      },
    ],
    [getProjectStepPriorityTypeTranslated]
  );

  const getProjectStepResponsibleContactTypeTranslated = useCallback(
    (projectStepResponsibleContactType: number) =>
      ({
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customer',
          defaultMessage: 'Müşteri',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.idecon',
          defaultMessage: 'Idecon',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customerAndIdecon',
          defaultMessage: 'Müşteri / Idecon',
        }),
      })[projectStepResponsibleContactType],
    [intl]
  );

  const RESPONSIBLE_CONTACT_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(
          PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON
        ),
      },
    ],
    [getProjectStepResponsibleContactTypeTranslated]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => products.filter((project) => project?.id != null).map((project) => project.id),
        getOptionLabel: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        valueFormatter: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        minWidth: 200,
      },
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projectStepManaging.projectStep', defaultMessage: 'Proje Adımı' }),
        minWidth: 250,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.stepExplanation',
          defaultMessage: 'Adım Açıklaması',
        }),
        minWidth: 250,
      },
      {
        field: 'estimatedTaskDay',
        headerName: intl.formatMessage({
          id: 'productProjectPlan.estimatedTaskDay',
          defaultMessage: 'Tahmini Adım Süresi (gün)',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 200,
      },
      {
        field: 'priority',
        headerName: intl.formatMessage({ id: 'projectStepManaging.priority', defaultMessage: 'Öncelik' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => PRIORITY_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          const priority = params.value ?? params.row?.priority ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT;
          const colorKey =
            priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
              ? 'success'
              : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
                ? 'warning'
                : 'error';

          return (
            <Chip
              size="small"
              label={getProjectStepPriorityTypeTranslated(priority)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
        minWidth: 200,
      },
      {
        field: 'responsibleContactType',
        headerName: intl.formatMessage({ id: 'productProjectPlan.responsibleContact', defaultMessage: 'Sorumlu' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => RESPONSIBLE_CONTACT_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? '',
        minWidth: 200,
      },
    ],
    [intl, products, theme, PRIORITY_OPTIONS, RESPONSIBLE_CONTACT_OPTIONS, getProjectStepPriorityTypeTranslated]
  );

  const handleCloseCreateProductProjectPlanDialog = () => setOpenCreateProductProjectPlanDialog(false);
  const handleClickOpenCreateProductProjectPlanDialog = () => setOpenCreateProductProjectPlanDialog(true);

  const handleCloseUpdateProductProjectPlanDialog = () => setOpenUpdateProductProjectPlanDialog(false);
  const handleClickOpenUpdateProductProjectPlanDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenUpdateProductProjectPlanDialog(true);
  };

  const handleCloseDeleteProductProjectPlanDialog = () => setOpenDeleteProductProjectPlanDialog(false);
  const handleClickOpenDeleteProductProjectPlanDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenDeleteProductProjectPlanDialog(true);
  };

  const getProductProjectPlans = useCallback(async () => {
    const postData: { projectId: number | null } = {
      projectId: null,
    };

    try {
      setLoading(true);

      const response = await apiService.getProductProjectPlans(postData);

      if (response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) =>
          (products.find((product) => product.id === a.projectId)?.title || '').localeCompare(
            products.find((product) => product.id === b.projectId)?.title || ''
          )
        );
        setProductProjectPlans(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [products]);

  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-project-task-base')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getProductProjectPlans();
  }, [getProductProjectPlans]);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-project-task-base']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenCreateProductProjectPlanDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage
                id="productProjectPlan.createProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Oluştur"
              />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['update-project-task-base']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenUpdateProductProjectPlanDialog}
              fullWidth
              size="medium"
              variant="contained"
              color="info"
              endIcon={<EditIcon />}>
              <FormattedMessage
                id="productProjectPlan.updateProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Güncelle"
              />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-project-task-base']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenDeleteProductProjectPlanDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage
                id="productProjectPlan.deleteProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Sil"
              />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>

      <br />

      <DataGridBox>
        <ZebraDataGrid
          pagination
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={productProjectPlans}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
        />
      </DataGridBox>

      <CreateProductProjectPlanDialog
        open={openCreateProductProjectPlanDialog}
        handleClose={handleCloseCreateProductProjectPlanDialog}
        products={products}
        priorityOptions={PRIORITY_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProductProjectPlans}
      />
      <UpdateProductProjectPlanDialog
        open={openUpdateProductProjectPlanDialog}
        handleClose={handleCloseUpdateProductProjectPlanDialog}
        productProjectPlanId={rowSelectionModel[0]}
        products={products}
        priorityOptions={PRIORITY_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProductProjectPlans}
      />
      <DeleteProductProjectPlanDialog
        open={openDeleteProductProjectPlanDialog}
        productProjectPlanId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProductProjectPlanDialog}
        refresh={getProductProjectPlans}
        setRowSelectionModel={setRowSelectionModel}
      />
    </>
  );
};

export default ProductProjectPlan;
